import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  OutlinedInput,
  Chip,
  Divider,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const Feedback = (props) => {
  const { t } = useTranslation();

  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [like, setLike] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [comment, setComment] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isEnglish, setIsEnglish] = useState(false);

  useEffect(() => {
    const browserLang = navigator.language || navigator.languages[0];
    if (browserLang.startsWith("en")) {
      setIsEnglish(true);
      console.log(isEnglish)
    }
  }, [isEnglish]);

  // Reset feedback when retrieval/assistant changes
  useEffect(() => {
    setFeedbackSent(false);
    resetForm();
  }, [props.retrievalId, props.selectedRetrieval]);

  // Add event listener to reset feedback when a new chat is started
  useEffect(() => {
    const resetFeedbackState = () => {
      setFeedbackSent(false);
      setLiked(false);
      setDisliked(false);
      setLike(false);
      setComment("");
    };

    document.addEventListener("resetFeedbackState", resetFeedbackState);
    return () => {
      document.removeEventListener("resetFeedbackState", resetFeedbackState);
    };
  }, []);

  const handleLike = () => {
    setLiked(true);
    setDisliked(false);
    setLike(true);
  };

  const handleDislike = () => {
    setLiked(false);
    setDisliked(true);
    setLike(false);
  };

  const handleOpenFeedback = () => {
    if (!props.disabled && !feedbackSent) {
      setShowConfirmationDialog(true);
    }
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const resetForm = () => {
    setLiked(false);
    setDisliked(false);
    setLike(false);
    setComment("");
  };

  const sendFeedback = async (id) => {
    if (id === null) {
      console.log("Es necesario preguntar antes de dar feedback");
      closeWindow();
      return;
    }

    try {
      setLoading(true);
      await props.addFeedback(id, like, comment);
      console.log("Feedback enviado");
      setFeedbackSent(true);
      setShowConfirmationDialog(false);
      resetForm();
    } catch {
      console.error("Error al enviar feedback:");
      setShowConfirmationDialog(false);
    } finally {
      setLoading(false);
    }
  };

  const closeWindow = () => {
    setShowConfirmationDialog(false);
  };

  const closeWindowAndSendFeedback = () => {
    sendFeedback(props.id);
  };

  const handleKeyDown = (e, submitFn) => {
    // Only submit on Enter if Shift key is not pressed (to allow for multiline comments)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitFn();
    }
  };

  // Styles for the feedback chip
  const feedbackChipStyle = {
    borderColor:
      props.colors && props.colors.secondaryColor
        ? props.colors.secondaryColor
        : "black",
    borderRadius: "16px",
    height: "28px",
    paddingX: "4px",
    textTransform: "none",
    opacity: props.disabled || feedbackSent ? 0.5 : 1,
    backgroundColor: feedbackSent ? "#e8f5e9" : "transparent",
    "& .MuiChip-icon": {
      color: feedbackSent
        ? "#4caf50"
        : props.colors && props.colors.secondaryColor
        ? props.colors.secondaryColor
        : "black",
    },
    "& .MuiChip-label": {
      color: feedbackSent
        ? "#4caf50"
        : props.colors && props.colors.secondaryColor
        ? props.colors.secondaryColor
        : "black",
    },
    "&:hover": {
      backgroundColor:
        props.disabled || feedbackSent
          ? feedbackSent
            ? "#e8f5e9"
            : "transparent"
          : (props.colors && props.colors.primaryColor
              ? props.colors.primaryColor
              : "black") + " !important",
      "& .MuiChip-icon, & .MuiChip-label": {
        color:
          props.disabled || feedbackSent
            ? feedbackSent
              ? "#4caf50"
              : "inherit"
            : "white",
      },
    },
  };

  // Styles for thumbs up/down buttons in dialog
  const thumbButtonStyle = (isActive) => ({
    borderRadius: "8px",
    padding: "8px",
    minWidth: "80px",
    border: `1px solid ${
      props.colors && props.colors.borderColor
        ? props.colors.borderColor
        : "black"
    }`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: "1",
    margin: "0 6px",
    backgroundColor: isActive
      ? props.colors && props.colors.primaryColor
        ? props.colors.primaryColor
        : "black"
      : "transparent",
    color: isActive ? "white" : "black",
    "&:hover": {
      backgroundColor: isActive
        ? props.colors && props.colors.primaryColor
          ? props.colors.primaryColor
          : "black"
        : "rgba(0, 0, 0, 0.04)",
    },
    "&:focus": {
      outline: "none",
      boxShadow: `0 0 0 2px ${
        props.colors && props.colors.primaryColor
          ? props.colors.primaryColor + "30"
          : "rgba(0,0,0,0.2)"
      }`,
    },
    "& .MuiSvgIcon-root": {
      color: isActive
        ? "white"
        : props.colors && props.colors.primaryColor
        ? props.colors.primaryColor
        : "black",
    },
  });

  // Dialog button styles
  const dialogButtonStyle = (isPrimary) => ({
    borderRadius: "8px",
    textTransform: "none",
    borderColor:
      props.colors && props.colors.borderColor
        ? props.colors.borderColor
        : "black",
    color: isPrimary
      ? "white"
      : props.colors && props.colors.borderColor
      ? props.colors.borderColor
      : "black",
    backgroundColor: isPrimary
      ? props.colors && props.colors.primaryColor
        ? props.colors.primaryColor
        : "black"
      : "transparent",
    "&:hover": {
      backgroundColor: isPrimary
        ? props.colors && props.colors.primaryColor
          ? props.colors.primaryColor
          : "black"
        : "#f5f5f5",
      borderColor:
        props.colors && props.colors.borderColor
          ? props.colors.borderColor
          : "black",
    },
    "&:focus": {
      boxShadow: `0 0 0 2px ${
        props.colors && props.colors.primaryColor
          ? props.colors.primaryColor + "30"
          : "rgba(0,0,0,0.2)"
      }`,
    },
  });

  return (
    <div>
      {/* Feedback Chip */}
      <Chip
        variant="outlined"
        icon={
          feedbackSent ? (
            <CheckCircleOutlineIcon fontSize="small" />
          ) : (
            <CampaignOutlinedIcon fontSize="small" />
          )
        }
        label={feedbackSent ? t("feedbackSent") : t("feedback")}
        onClick={handleOpenFeedback}
        disabled={props.disabled || feedbackSent}
        size="small"
        sx={feedbackChipStyle}
      />

      {/* Feedback Dialog */}
      <Dialog
        open={showConfirmationDialog}
        onClose={() => closeWindow()}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "0.5rem",
            width: "95%",
            maxWidth: "450px",
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)", // matches shadow-lg
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color:
              props.colors && props.colors.nameColor
                ? props.colors.nameColor
                : "primary",
            padding: "16px 20px",
            textAlign: "center",
          }}
        >
          {t("feedbackTitle")}
        </DialogTitle>
        <DialogContent sx={{ padding: "0 20px" }}>
          {/* Thumbs up/down buttons */}
          <div className="flex justify-center items-center mb-3 w-full gap-3">
            <Button
              sx={thumbButtonStyle(liked)}
              onClick={handleLike}
              variant="outlined"
              fullWidth
            >
              <ThumbUpIcon />
            </Button>
            <Button
              sx={thumbButtonStyle(disliked)}
              onClick={handleDislike}
              variant="outlined"
              fullWidth
            >
              <ThumbDownIcon />
            </Button>
          </div>

          {/* Divider */}
          <Divider sx={{ my: 2, borderColor: "#e0e0e0" }} />

          {/* Comment text field */}
          <div className="flex flex-col bg-white border rounded">
            <OutlinedInput
              type="text"
              placeholder={t("feedbackComment")}
              value={comment}
              onChange={handleCommentChange}
              onKeyDown={(e) => handleKeyDown(e, closeWindowAndSendFeedback)}
              multiline
              rows={3}
              fullWidth
              sx={{
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    props.colors && props.colors.borderColor
                      ? props.colors.borderColor
                      : "black",
                  borderWidth: "1px",
                },
              }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 20px" }}>
          <Button
            variant="outlined"
            onClick={() => {
              resetForm();
              closeWindow();
            }}
            sx={dialogButtonStyle(false)}
            disabled={loading}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => closeWindowAndSendFeedback()}
            sx={dialogButtonStyle(true)}
            disabled={loading}
          >
            {loading ? t("sending") : t("send")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Feedback;
